import React, {useCallback, useEffect, useRef, useState} from "react"
import {findDOMNode} from "react-dom"

import {LexicalEditor} from "lexical"
import PopupMenuButton from "../../../../../popover-menu/popup-menu-button/popup-menu-button"
import ToolbarHeadingButton from "./heading-button"
import ToolbarHeadingMenu from "./heading-menu"
import {BannerType} from "../../../banner/banner-plugin"
import {getBannerStyles} from "../../../banner/node/banner-node"

type Props = {
    editor      : LexicalEditor
    blockType   : null | string
}

const MINIMIZE_THRESHOLD_WIDTH = 70
const MAXIMIZE_THRESHOLD_WIDTH = 50

const debug = false
// const debug = "HeadingToolbar"

export default function HeadingToolbar(props: Props): JSX.Element {

    const [isMinimized, setIsMinimized] = useState(false)

    const elementRef: any = useRef()

    const onWidthChanged = useCallback(()=>{
        const node:any = findDOMNode(elementRef.current)
        if (!node) return

        debug && console.log(debug, 'onWidthChanged', isMinimized, node.offsetWidth,
            node.offsetWidth > MAXIMIZE_THRESHOLD_WIDTH,
            node.offsetWidth < MINIMIZE_THRESHOLD_WIDTH)

        if (isMinimized) {
            if (node.offsetWidth > MAXIMIZE_THRESHOLD_WIDTH) {
                setIsMinimized(false)
            }
        } else {
            if (node.offsetWidth < MINIMIZE_THRESHOLD_WIDTH) {
                setIsMinimized(true)
            }
        }
    }, [isMinimized])

    useEffect(() => {
        const unsubscribe = gd.bus.subscribeNoContext('lexical-toolbar:width-changed', onWidthChanged)
        return () => {
            unsubscribe()
        }
    }, [isMinimized])

    const iconComponent = (bannerType: BannerType) =>
        <div className="ico editor-i5-banner fs-16" style={_.pick(getBannerStyles(bannerType), 'color')}/>

    const options = [
        {value: 'paragraph',    icon: 'editor-i5-normal-text',      label: 'Normal'},
        {value: 'h1',           icon: 'editor-i5-heading-1',        label: 'Heading 1'},
        {value: 'h2',           icon: 'editor-i5-heading-2',        label: 'Heading 2'},
        {value: 'h3',           icon: 'editor-i5-heading-3',        label: 'Heading 3'},
        'separator',
        {
            type    : 'popover',
            icon    : 'editor-i5-banner',
            label   : 'Banner',
            options : [
                {value: BannerType.LIGHT_BLUE,  iconComponent: iconComponent(BannerType.LIGHT_BLUE),    label: 'Light blue'},
                {value: BannerType.DARK_BLUE,   iconComponent: iconComponent(BannerType.DARK_BLUE),     label: 'Dark blue'},
                {value: BannerType.GREEN,       iconComponent: iconComponent(BannerType.GREEN),         label: 'Green'},
                {value: BannerType.YELLOW,      iconComponent: iconComponent(BannerType.YELLOW),        label: 'Yellow'},
                {value: BannerType.ORANGE,      iconComponent: iconComponent(BannerType.ORANGE),        label: 'Orange'},
                {value: BannerType.RED,         iconComponent: iconComponent(BannerType.RED),           label: 'Red'},
                {value: BannerType.MAGENTA,     iconComponent: iconComponent(BannerType.MAGENTA),       label: 'Magenta'},
                {value: BannerType.PURPLE,      iconComponent: iconComponent(BannerType.PURPLE),        label: 'Purple'},
                {value: BannerType.BROWN,       iconComponent: iconComponent(BannerType.BROWN),         label: 'Brown'},
                {value: BannerType.GRAY,        iconComponent: iconComponent(BannerType.GRAY),          label: 'Gray'},
            ]
        },
        {value: 'code',         icon: 'editor-i5-code',             label: 'Code block'},
        {value: 'quote',        icon: 'editor-i5-quote',            label: 'Quote block'},
        'separator',
        {value: 'bullet',       icon: 'editor-i5-bullet-list',      label: 'Bullet list'},
        {value: 'number',       icon: 'editor-i5-numbered-list',    label: 'Numbered list'},
        {value: 'check',        icon: 'editor-i5-check-list',       label: 'Check list'},
    ]

    debug && console.log(debug, 'render', isMinimized, props)

    return (
        <PopupMenuButton ref                = {elementRef}
                         className          = "heading-toolbar"
                         style              = {{minWidth: MAXIMIZE_THRESHOLD_WIDTH}}
                         hint               = "Select style"
                         btnComponent       = {(state:any,props:any)=><ToolbarHeadingButton {...props} {...state} isMinimized={isMinimized}/>}
                         menuComponent      = {ToolbarHeadingMenu}
                         menuParams         = {{
                             options    : options,
                             editor     : props.editor,
                             blockType  : props.blockType,
                         }}/>
    )
}